import { useCallback, useMemo, useReducer } from 'react'
import type { StreamCallbacks } from '../../services/teachingAgent'
import { initialTrace, traceReducer, TraceState } from './traceState'

export interface UseThinkingTrace {
  trace: TraceState
  /** Call when a send starts (resets and starts the clock). */
  begin: () => void
  reset: () => void
  /** Mark the in-flight turn stopped (Stop pressed / aborted). */
  markStopped: () => void
  /** Spread into sendMessageStreaming callbacks; compose with your own handlers. */
  callbacks: {
    onStatus: NonNullable<StreamCallbacks['onStatus']>
    onReasoning: (text: string) => void
    /** Call on every delta; only the first one matters. */
    onContent: () => void
    onEnd: () => void
    onError: () => void
    onAbort: () => void
  }
}

export function useThinkingTrace(now: () => number = Date.now): UseThinkingTrace {
  const [trace, dispatch] = useReducer(traceReducer, initialTrace)
  const begin = useCallback(() => dispatch({ type: 'begin', at: now() }), [now])
  const reset = useCallback(() => dispatch({ type: 'reset' }), [])
  const markStopped = useCallback(() => dispatch({ type: 'abort', at: now() }), [now])
  const callbacks = useMemo(() => ({
    onStatus: (s: { phase: 'thinking' | 'tool' | 'writing'; label?: string }) =>
      dispatch({ type: 'status', at: now(), phase: s.phase, label: s.label }),
    onReasoning: (text: string) => dispatch({ type: 'reasoning', at: now(), text }),
    onContent: () => dispatch({ type: 'delta', at: now() }),
    onEnd: () => dispatch({ type: 'done', at: now() }),
    onError: () => dispatch({ type: 'error', at: now() }),
    onAbort: () => dispatch({ type: 'abort', at: now() }),
  }), [now])
  return { trace, begin, reset, markStopped, callbacks }
}
